// ---------------------------------------------------------------------------
// DSS-32: Grid controls — visual CSS Grid editor
// ---------------------------------------------------------------------------
import { useCallback, useId, useMemo } from "react";
import { Grid3X3 } from "lucide-react";
import { sanitizeCssValue } from "../style-state-system";

// ── Types ──
export type RowMode = "auto" | "explicit" | "custom";
export type GapUnit = "px" | "rem" | "%";

export interface GapValue {
  readonly value: number;
  readonly unit: GapUnit;
}

export interface GridConfig {
  readonly columns: number;
  readonly customColumns: string;
  readonly rowMode: RowMode;
  readonly rowCount: number;
  readonly customRows: string;
  readonly rowGap: GapValue;
  readonly columnGap: GapValue;
  readonly templateAreas: string;
}

// ── Constants ──
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 12;
const MIN_ROWS = 1;
const MAX_ROWS = 24;
const MIN_GAP = 0;
const MAX_GAP = 999;
const GAP_UNITS: readonly GapUnit[] = ["px", "rem", "%"];
const ROW_MODES: readonly { value: RowMode; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "explicit", label: "Count" },
  { value: "custom", label: "Custom" },
];
const MAX_TEMPLATE_AREAS_LENGTH = 2000;

const DEFAULT_GAP: GapValue = { value: 0, unit: "px" };

const DEFAULT_CONFIG: GridConfig = {
  columns: 3,
  customColumns: "",
  rowMode: "auto",
  rowCount: 1,
  customRows: "",
  rowGap: DEFAULT_GAP,
  columnGap: DEFAULT_GAP,
  templateAreas: "",
};

// ── NaN guards ──
function safeInt(val: number, min: number, max: number): number {
  const n = Number(val);
  if (Number.isNaN(n)) return min;
  return Math.min(Math.max(Math.round(n), min), max);
}

function safeGap(val: number): number {
  return safeInt(val, MIN_GAP, MAX_GAP);
}

// ── CSS generator ──
export function generateGridCss(config: GridConfig): string {
  const lines: string[] = ["display: grid;"];

  // Columns
  const colTemplate = sanitizeCssValue(config.customColumns.trim());
  if (colTemplate) {
    lines.push(`grid-template-columns: ${colTemplate};`);
  } else {
    const cols = safeInt(config.columns, MIN_COLUMNS, MAX_COLUMNS);
    lines.push(`grid-template-columns: repeat(${cols}, 1fr);`);
  }

  // Rows
  if (config.rowMode === "explicit") {
    const rows = safeInt(config.rowCount, MIN_ROWS, MAX_ROWS);
    lines.push(`grid-template-rows: repeat(${rows}, 1fr);`);
  } else if (config.rowMode === "custom" && config.customRows.trim()) {
    lines.push(`grid-template-rows: ${sanitizeCssValue(config.customRows.trim())};`);
  }

  // Template areas
  const areas = sanitizeCssValue(config.templateAreas.trim());
  if (areas) {
    lines.push(`grid-template-areas: ${areas};`);
  }

  // Gap
  if (config.rowGap.value > 0) {
    lines.push(`row-gap: ${safeGap(config.rowGap.value)}${config.rowGap.unit};`);
  }
  if (config.columnGap.value > 0) {
    lines.push(`column-gap: ${safeGap(config.columnGap.value)}${config.columnGap.unit};`);
  }

  return lines.join("\n");
}

// ── Gap input sub-component ──
function GapInput({
  label,
  value,
  onChange,
  id,
}: {
  readonly label: string;
  readonly value: GapValue;
  readonly onChange: (v: GapValue) => void;
  readonly id: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[10px] font-medium text-[var(--color-muted-foreground)]">
        {label}
      </label>
      <div className="flex gap-1">
        <input
          id={id}
          type="number"
          min={MIN_GAP}
          max={MAX_GAP}
          value={value.value}
          onChange={(e) => onChange({ ...value, value: safeGap(Number(e.target.value)) })}
          className="h-7 w-16 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
          aria-label={label}
        />
        <select
          value={value.unit}
          onChange={(e) => onChange({ ...value, unit: e.target.value as GapUnit })}
          className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1 text-[10px] text-[var(--color-foreground)]"
          aria-label={`${label} unit`}
        >
          {GAP_UNITS.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ── Props ──
interface GridControlsProps {
  readonly value: GridConfig | undefined;
  readonly onChange: (config: GridConfig) => void;
}

// ── Component ──
export default function GridControls({ value, onChange }: GridControlsProps) {
  const config = value ?? DEFAULT_CONFIG;
  const baseId = useId();

  const update = useCallback(
    <K extends keyof GridConfig>(key: K, val: GridConfig[K]) => {
      onChange({ ...config, [key]: val });
    },
    [config, onChange],
  );

  // Mini-grid preview: compute cells
  const previewCols = config.customColumns.trim()
    ? Math.min(config.customColumns.trim().split(/\s+/).length, MAX_COLUMNS)
    : safeInt(config.columns, MIN_COLUMNS, MAX_COLUMNS);
  const previewRows = config.rowMode === "explicit"
    ? safeInt(config.rowCount, MIN_ROWS, Math.min(config.rowCount, 6))
    : Math.min(Math.ceil(6 / previewCols), 4);
  const cellCount = previewCols * previewRows;

  const previewGridStyle = useMemo(
    () => ({
      display: "grid" as const,
      gridTemplateColumns: `repeat(${previewCols}, 1fr)`,
      gridTemplateRows: `repeat(${previewRows}, 1fr)`,
      gap: "2px",
    }),
    [previewCols, previewRows],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Grid3X3 className="h-3.5 w-3.5" />
        Grid Layout
      </div>

      {/* Mini-grid preview */}
      <div
        className="h-20 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-2"
        style={previewGridStyle}
        aria-label={`Grid preview: ${previewCols} columns, ${previewRows} rows`}
      >
        {Array.from({ length: Math.min(cellCount, 48) }, (_, i) => (
          <div
            key={i}
            className="rounded-sm bg-[var(--color-primary)]/20"
          />
        ))}
      </div>

      {/* Columns */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${baseId}-cols`}
          className="text-[10px] font-medium text-[var(--color-muted-foreground)]"
        >
          Columns ({MIN_COLUMNS}-{MAX_COLUMNS})
        </label>
        <input
          id={`${baseId}-cols`}
          type="number"
          min={MIN_COLUMNS}
          max={MAX_COLUMNS}
          value={config.columns}
          onChange={(e) => update("columns", safeInt(Number(e.target.value), MIN_COLUMNS, MAX_COLUMNS))}
          disabled={config.customColumns.trim().length > 0}
          className="h-7 w-20 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)] disabled:opacity-50"
        />
        <label
          htmlFor={`${baseId}-custom-cols`}
          className="text-[10px] text-[var(--color-muted-foreground)]"
        >
          Custom template (overrides count)
        </label>
        <input
          id={`${baseId}-custom-cols`}
          type="text"
          placeholder="e.g. 1fr 2fr 1fr"
          value={config.customColumns}
          onChange={(e) => update("customColumns", e.target.value)}
          className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
          aria-label="Custom column template"
        />
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">Rows</span>
        <div className="flex gap-1">
          {ROW_MODES.map((mode) => (
            <button
              key={mode.value}
              type="button"
              aria-pressed={config.rowMode === mode.value}
              onClick={() => update("rowMode", mode.value)}
              className={[
                "rounded-md border px-2 py-1 text-[10px] font-medium transition-colors",
                config.rowMode === mode.value
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
              ].join(" ")}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {config.rowMode === "explicit" && (
          <input
            id={`${baseId}-row-count`}
            type="number"
            min={MIN_ROWS}
            max={MAX_ROWS}
            value={config.rowCount}
            onChange={(e) => update("rowCount", safeInt(Number(e.target.value), MIN_ROWS, MAX_ROWS))}
            className="h-7 w-20 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
            aria-label="Row count"
          />
        )}

        {config.rowMode === "custom" && (
          <input
            id={`${baseId}-custom-rows`}
            type="text"
            placeholder="e.g. auto 200px 1fr"
            value={config.customRows}
            onChange={(e) => update("customRows", e.target.value)}
            className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
            aria-label="Custom row template"
          />
        )}
      </div>

      {/* Gap */}
      <div className="flex gap-3">
        <GapInput
          label="Row gap"
          value={config.rowGap}
          onChange={(v) => update("rowGap", v)}
          id={`${baseId}-row-gap`}
        />
        <GapInput
          label="Col gap"
          value={config.columnGap}
          onChange={(v) => update("columnGap", v)}
          id={`${baseId}-col-gap`}
        />
      </div>

      {/* Template areas (advanced) */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${baseId}-areas`}
          className="text-[10px] font-medium text-[var(--color-muted-foreground)]"
        >
          Template areas (advanced)
        </label>
        <textarea
          id={`${baseId}-areas`}
          placeholder={`"header header"\n"sidebar main"\n"footer footer"`}
          value={config.templateAreas}
          onChange={(e) => {
            const clamped = e.target.value.slice(0, MAX_TEMPLATE_AREAS_LENGTH);
            update("templateAreas", clamped);
          }}
          rows={3}
          className="resize-y rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 font-mono text-[10px] text-[var(--color-foreground)]"
          aria-label="Grid template areas"
        />
      </div>

      {/* CSS preview */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateGridCss(config)}
        </code>
      </div>
    </div>
  );
}
